import { Box, Grid, Typography, Tooltip, Stack } from '@mui/material';
import { useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DynamicHoverMenu from 'components/Common/DynamicHoverMenu';
import { useHeader } from "Context/contexts/HeaderTabRightContentContext";

export type HeaderTabItem = {
	label: string;
	path: string;
	capability?: string | string[];
	activeMatch?: (pathname: string) => boolean; // optional; defaults to pathname.includes(path)
	tooltipContent?: string; // optional tooltip content
	showTooltip?: boolean; // whether to show eye icon with tooltip
	children?: HeaderTabItem[]
};

export type HeaderTabsProps = {
	items: HeaderTabItem[];
	capabilities?: string[]; // pass your user's capabilities
	left?: number; // fixed left offset (default 75)
	right?: number; // fixed right offset (default 15)
	zIndex?: number; // default 10
	topPadding?: number; // default 1
	showDivider?: boolean; // default true
	activeColorToken?: string; // MUI palette token, default 'text.focus'
	inactiveColorToken?: string; // default 'text.main'
	fixed?: boolean; // default true (position: fixed)
	disabled?: boolean;
	rightContent?: React.ReactNode; // content rendered on the right side of the tab bar
};

const can = (caps: string[] | undefined, need?: string | string[]) => {
	if (!need) return true;
	if (!caps) return false;
	const arr = Array.isArray(need) ? need : [need];
	return arr.some((n) => caps.includes(n));
};

export default function HeaderTabs({
	items,
	capabilities,
	left = 75,
	right = 15,
	zIndex = 10,
	topPadding = 1,
	showDivider = true,
	activeColorToken,
	inactiveColorToken,
	fixed = true,
	disabled = false,
	rightContent
}: HeaderTabsProps) {
	const theme = useTheme();
	const colors = theme.palette;
	const location = useLocation();
	const navigate = useNavigate();

	const { rightContextContent } = useHeader();


	const activeColor = activeColorToken || colors.textLight.dark;
	const inactiveColor = inactiveColorToken || colors.textLight.secondary;

	const visibleItems = items.filter((it) => can(capabilities, it.capability));

	const tabItems = (visibleItems: HeaderTabItem[], direction?: 'row' | 'column') => <Grid
		container
		spacing={2}
		wrap="nowrap"
		sx={{
			width: 'max-content',
			minWidth: '100%',
			flexDirection: direction || 'row',
			overflow: 'auto',
			whiteSpace: 'nowrap',
			'&::-webkit-scrollbar': { height: '3px' },
			'&::-webkit-scrollbar-thumb': { backgroundColor: colors.scrollbar.thumb, borderRadius: 4 },
			'&::-webkit-scrollbar-track': { backgroundColor: colors.scrollbar.background },
		}}
		direction={direction || 'row'}
	>
		{visibleItems.map((tab, index) => {
			const isActive = tab.activeMatch
				? tab.activeMatch(location.pathname)
				: location.pathname.includes(tab.path);

			const isTabDisabled = disabled && isActive;

			const activeChild = tab.children?.find(child =>
				child.activeMatch ? child.activeMatch(location.pathname) : false
			);
			const displayLabel = activeChild ? activeChild.label : tab.label;

			return (
				<Grid item key={`${tab.path}-${index}`} >
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: 1,
							color: isActive ? activeColor : inactiveColor,
							borderBottom: isActive ? `4px solid ${activeColor} !important` : 'none',
							cursor: isTabDisabled ? 'not-allowed' : 'pointer',
						}}
						onClick={(e) => {
							if (isTabDisabled) {
								e.preventDefault();
								e.stopPropagation();
								return;
							}

							navigate(activeChild ? activeChild.path : tab.path)
						}}
					>
						{
							!tab.children?.length
								? <><Typography
									variant='tabPageHeader'
								>
									{tab.label}
								</Typography>
									{tab.showTooltip && tab.tooltipContent && (
										<Tooltip
											title={
												<Box sx={{ p: 1 }}>
													<Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
														About Dashboard
													</Typography>
													<Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
														{tab.tooltipContent}
													</Typography>
												</Box>
											}
											arrow
											placement="bottom"
											componentsProps={{
												tooltip: {
													sx: {
														bgcolor: 'white',
														color: 'text.primary',
														boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
														maxWidth: 400,
														fontSize: '0.875rem',
														'& .MuiTooltip-arrow': {
															color: 'white'
														}
													}
												}
											}}
										>
											<VisibilityIcon
												sx={{
													fontSize: '1rem',
													color: isActive ? activeColor : inactiveColor,
													cursor: isTabDisabled ? 'not-allowed' : 'pointer',
													'&:hover': {
														opacity: isTabDisabled ? 1 : 0.8
													}
												}}
												onClick={(e) => {
													e.stopPropagation();
												}}
											/>
										</Tooltip>
									)}</>
								: <DynamicHoverMenu items={tab.children} disabled={isTabDisabled} label={
									<Stack direction='row' gap="10px">
										<Typography variant='tabPageHeader' sx={{ color: isActive ? activeColor : inactiveColor }}>
											{displayLabel}
										</Typography>
									</Stack>
								} />
						}
					</Box>
				</Grid>
			);
		})}
	</Grid>
	return (
		<Box
			sx={{
				position: fixed ? 'fixed' : 'relative',
				left,
				right,
				zIndex,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				minWidth: 0,
				overflow: 'hidden',
				gap: 1,
				pt: '4px'
			}}
		>
			{/* Scroll container */}
			<Box
				role="tablist"
				sx={{
					pt: 1,
					position: 'relative',
					width: rightContent || rightContextContent ? '50%' : '100%',
					flex: rightContent || rightContextContent ? '1 1 50%' : '1 1 100%',
					minWidth: 0,
					display: 'flex',
					alignItems: 'center',
					overflowX: 'auto',
					overflowY: 'hidden',
					whiteSpace: 'nowrap',
					'&::-webkit-scrollbar': { height: '3px' },
					'&::-webkit-scrollbar-thumb': { backgroundColor: colors.scrollbar.thumb, borderRadius: 4 },
					'&::-webkit-scrollbar-track': { backgroundColor: colors.scrollbar.background },
				}}
			>
				{tabItems(visibleItems)}
			</Box>

			{(rightContextContent || rightContent) && (
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						ml: 2,
						flex: '1 1 50%',
						minWidth: 0,
						overflowX: 'auto',
						overflowY: 'hidden',
						whiteSpace: 'nowrap',
						'&::-webkit-scrollbar': { height: '3px' },
						'&::-webkit-scrollbar-thumb': { backgroundColor: colors.scrollbar.thumb, borderRadius: 4 },
						'&::-webkit-scrollbar-track': { backgroundColor: colors.scrollbar.background },
					}}
				>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'flex-end',
							gap: 1,
							width: '100%',
							minWidth: 'max-content',
						}}
					>

						{rightContent && (
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: 1,
									zIndex: 1,
									flexShrink: 0,
								}}
							>
								{rightContent}
							</Box>
						)}

						{rightContextContent && (
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: 1,
									flexShrink: 0
								}}
							>
								{rightContextContent}
							</Box>
						)}
					</Box>
				</Box>
			)}
		</Box>
	);
}

const TabItem = () => {

}